import React, { useState, useEffect } from "react";
import { useLanguage } from "@/lib/LanguageContext";
// Relative, not "@/...": these two are the validation infrastructure, imported by every
// form that adopts it, and there is no "@" alias on the Jest side. A "@/" import here
// makes this component unresolvable in the existing tests of all six consumers unless each
// adds a virtual passthrough mock. Relative paths make Jest resolve the REAL modules —
// same reasoning as lib/api.js requiring "./apiErrors".
import { cn } from "../lib/utils";
import FieldError, { invalidInputClass } from "../components/ui/FieldError";

/**
 * LanguageAwareField Component
 *
 * Renders a dynamic content input bound to the currently selected dashboard language,
 * with language indicators (FR / EN presence badges) and tab controls for missing locales.
 *
 * @param {string} label - Input label
 * @param {string} valueFr - Value in French
 * @param {string} valueEn - Value in English
 * @param {function} onChangeFr - Handler for French updates
 * @param {function} onChangeEn - Handler for English updates
 * @param {boolean} multiline - Render textarea instead of input
 * @param {number} rows - Rows for multiline input
 * @param {number} maxLength - Character cap
 * @param {string} counterTestId - Test ID for counter element
 * @param {string} counterText - Counter text string
 * @param {string} testid - Test ID for input wrapper
 * @param {string} className - Optional container styling
 * @param {"fr"|"en"|"any"} [requiredLang] - Which locale the parent form requires:
 *   "fr" / "en" for that language specifically, "any" for at least one of the two
 *   (matching lib/validation.js's frRequiredField and withAnyLangRule). Omitted =
 *   optional field, rendered exactly as before. The visible marker sits next to `label`,
 *   so a field rendered without one carries the requirement through `aria-required` alone.
 * @param {string} [placeholderFr] / [placeholderEn] - Placeholder of each language tab
 *   (e.g. the sample text the public site shows while the field is empty). Omitted, the
 *   generic "Enter text in XX..." placeholder is kept.
 * @param {string} [error] - The field's validation message, ALREADY RESOLVED to text by
 *   the caller's `t` (state holds an i18n key — see lib/validation.js). Truthy switches
 *   the field into its invalid state.
 */
export default function LanguageAwareField({
  label,
  valueFr = "",
  valueEn = "",
  onChangeFr,
  onChangeEn,
  multiline = false,
  rows = 3,
  maxLength,
  counterTestId,
  counterText,
  testid = "language-aware-field",
  className = "",
  requiredLang,
  error,
  placeholderFr,
  placeholderEn,
}) {
  const langCtx = useLanguage();
  const globalLang = langCtx?.lang || "fr";

  // Local active tab defaults to global dashboard language, falling back to "en" if unsupported
  const [activeLang, setActiveLang] = useState(() => (globalLang === "fr" ? "fr" : "en"));

  // Keep active tab in sync if dashboard language toggles between fr and en
  useEffect(() => {
    if (globalLang === "fr" || globalLang === "en") {
      setActiveLang(globalLang);
    }
  }, [globalLang]);

  const hasFr = Boolean(valueFr && valueFr.trim().length > 0);
  const hasEn = Boolean(valueEn && valueEn.trim().length > 0);

  // Which language the requirement is unsatisfied in — the one a refused submit is
  // actually about. `null` when the requirement is met (or when there is none).
  // For "any", both being blank means EITHER would do, so the field points at the
  // dashboard language: the locale this practitioner writes in.
  let missingLang = null;
  if (requiredLang === "fr" && !hasFr) missingLang = "fr";
  else if (requiredLang === "en" && !hasEn) missingLang = "en";
  else if (requiredLang === "any" && !hasFr && !hasEn) missingLang = globalLang === "fr" ? "fr" : "en";

  // A language-aware field hides half its content behind a tab, so an error alone is not
  // enough: the practitioner would read "French content is required" while looking at a
  // filled EN input. Switching the tab puts the empty field they must fill in front of
  // them. Deps are [error] ONLY — this fires when the error STRING changes, never per
  // keystroke, so it cannot fight a practitioner who deliberately switched tabs to copy
  // their text across (missingLang and activeLang are read on purpose as they are at that
  // instant). Two consecutive refused submits with the identical message do NOT re-fire —
  // the dependency didn't change — so a practitioner who switches tabs away and resubmits
  // without fixing anything stays on the tab they switched to, not back on the missing one.
  useEffect(() => {
    if (error && missingLang) setActiveLang(missingLang);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error]);

  const invalidFr = Boolean(error) && missingLang === "fr";
  const invalidEn = Boolean(error) && missingLang === "en";
  const badgeClass = (has, invalid) => {
    if (invalid) return "bg-red-100 text-red-700";
    return has ? "bg-emerald-100 text-emerald-800" : "bg-eh-border-soft text-eh-muted";
  };

  const currentValue = activeLang === "fr" ? valueFr : valueEn;
  const handleChange = (newVal) => {
    if (activeLang === "fr") {
      onChangeFr?.(newVal);
    } else {
      onChangeEn?.(newVal);
    }
  };

  return (
    <div className={`space-y-1.5 ${className}`} data-testid={testid}>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          {label && (
            <label className="text-xs uppercase tracking-[0.18em] text-eh-muted font-medium">
              {label}
              {requiredLang && (
                // Language-free glyph on purpose: this component has no `t` binding, and
                // an English word here would be English in the FR and ES dashboards. The
                // requirement reaches assistive technology through aria-required on the
                // input plus the role="alert" message, not through this decoration.
                <span className="text-red-600 ml-0.5" aria-hidden="true" data-testid={`${testid}-required`}>*</span>
              )}
            </label>
          )}
          {/* Presence Badges */}
          <div className="flex items-center gap-1">
            <span
              className={cn(
                "text-[10px] px-1.5 py-0.5 rounded-full font-mono font-semibold transition-colors",
                badgeClass(hasFr, invalidFr),
              )}
              title={hasFr ? "French content provided" : "French content missing"}
              data-testid={`${testid}-badge-fr`}
            >
              FR {hasFr && "✓"}
            </span>
            <span
              className={cn(
                "text-[10px] px-1.5 py-0.5 rounded-full font-mono font-semibold transition-colors",
                badgeClass(hasEn, invalidEn),
              )}
              title={hasEn ? "English content provided" : "English content missing"}
              data-testid={`${testid}-badge-en`}
            >
              EN {hasEn && "✓"}
            </span>
          </div>
        </div>

        {/* Quick Language Switcher Tabs */}
        <div className="flex items-center gap-2">
          {maxLength != null && counterText && (
            <span className="text-xs text-eh-muted mr-2" data-testid={counterTestId}>
              {counterText}
            </span>
          )}
          <div className="flex items-center bg-eh-bg border border-eh-border-soft rounded-lg p-0.5 text-xs font-mono">
            <button
              type="button"
              onClick={() => setActiveLang("fr")}
              className={`px-2 py-0.5 rounded-md transition-all ${
                activeLang === "fr" ? "bg-white text-eh-ink shadow-sm font-bold" : "text-eh-muted hover:text-eh-ink"
              }`}
              data-testid={`${testid}-tab-fr`}
            >
              FR
            </button>
            <button
              type="button"
              onClick={() => setActiveLang("en")}
              className={`px-2 py-0.5 rounded-md transition-all ${
                activeLang === "en" ? "bg-white text-eh-ink shadow-sm font-bold" : "text-eh-muted hover:text-eh-ink"
              }`}
              data-testid={`${testid}-tab-en`}
            >
              EN
            </button>
          </div>
        </div>
      </div>

      {multiline ? (
        <textarea
          rows={rows}
          value={currentValue}
          maxLength={maxLength}
          onChange={(e) => handleChange(e.target.value)}
          placeholder={(activeLang === "fr" ? placeholderFr : placeholderEn) || `Enter text in ${activeLang.toUpperCase()}...`}
          aria-required={requiredLang ? true : undefined}
          aria-invalid={error ? true : undefined}
          className={cn(
            "w-full bg-white border border-eh-border-soft rounded-xl px-4 py-2.5 text-eh-ink focus:outline-none focus:border-eh-sage-deep resize-none text-sm leading-relaxed",
            error && invalidInputClass,
          )}
          data-testid={`${testid}-input`}
        />
      ) : (
        <input
          type="text"
          value={currentValue}
          maxLength={maxLength}
          onChange={(e) => handleChange(e.target.value)}
          placeholder={(activeLang === "fr" ? placeholderFr : placeholderEn) || `Enter text in ${activeLang.toUpperCase()}...`}
          aria-required={requiredLang ? true : undefined}
          aria-invalid={error ? true : undefined}
          className={cn(
            "w-full bg-white border border-eh-border-soft rounded-xl px-4 py-2.5 text-eh-ink focus:outline-none focus:border-eh-sage-deep text-sm",
            error && invalidInputClass,
          )}
          data-testid={`${testid}-input`}
        />
      )}
      <FieldError message={error} testId={`${testid}-error`} />
    </div>
  );
}
